import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Formik } from 'formik';
import { uniq } from 'lodash';
import LazyLoad from 'react-lazyload';
import * as Yup from 'yup';
import classNames from 'classnames';

import StepContainer from '../StepContainer';
import FindProviders from '../FindProviders';
import FixedFooter from '../Footer/FixedFooter';
import { WhatIfCard, ProviderCard, NoResultsCard } from './ProviderCards';

import { providersOperations } from '@evdy/web-redux/dist/lib/webPurchase/providers';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import styles from './SelectProvider.module.scss';

import chevronDown from '../../public/images/chevron-down-everdays-navy.svg';

const { fetchProviders } = providersOperations;
const { saveLastScreen, saveFuneralHomeToUser, resetSelectedFuneralHome } = authUserOperations;

const {
  'select-provider': selectProviderClass,
  'show-more-family': showMoreFamilyProvidersClass,
  'texas-disclaimer': texasDisclaimerClass
} = styles;

const SelectProvider = ({ location }) => {
  const dispatch = useDispatch();
  const {
    authUser,
    providersObj,
    preneedPackages = []
  } = useSelector(({ authUser, webPurchase }) => ({
    authUser: authUser,
    providersObj: webPurchase?.providers,
    preneedPackages: webPurchase?.preneedPackages?.data?.packages
  }));
  const {
    _id: userId,
    address: { zip = '' } = {},
    preneed: {
      packageId: selectedPackageId,
      funeralHome: { _id: selectedHomeId } = {},
      userInfo: { address: { zip: preneedZip } = {} } = {},
      preSelectedCompany
    } = {}
  } = authUser?.data ?? {};
  const userZip = zip || preneedZip;
  const { isFetching, funeralHomeSelected } = authUser ?? {};
  // providers vars
  const { stateWarning, array: providers, isFetching: isFetchingProviders } = providersObj ?? {};
  const isTexasResident = !!stateWarning?.url;

  const servedFamilyProviders = useMemo(() => {
    return providers.filter(({ servedFamily }) => !!servedFamily);
  }, [providers]);
  const [familyProvidersToShow, setFamilyProvidersToShow] = useState(1);

  const providersInRange = useMemo(() => {
    // filter out dupes from servedFamilyProvidersArray (remove filter when kb fixes end point/when we merge dev into project branch)
    // sort remaining providers by average rating
    const servedFamilyHomeIds = uniq(servedFamilyProviders.map(({ _id }) => _id.toString()));
    return providers
      .filter(({ servedFamily, _id }) => {
        if (!!servedFamily) return false;
        if (servedFamilyHomeIds.includes(_id.toString())) return false;
        return true;
      })
      .sort((a, b) => {
        if (!a?.ratingsInfo?.average) return 1;
        if (!b?.ratingsInfo?.average) return -1;
        return a?.ratingsInfo?.average < b?.ratingsInfo?.average ? 1 : -1;
      });
  }, [providers]);
  // splice in WhatIfCard as 3rd element in array if there are results
  if (!!providersInRange.length && !providersInRange.includes('WhatIfCard'))
    providersInRange.splice(2, 0, 'WhatIfCard');

  // current package
  const currentPackage =
    preneedPackages.find(({ packageId }) => packageId === selectedPackageId) ?? {};
  const { colors: { color1: headerColor, color2: fillColor } = {}, packageId = '' } =
    currentPackage;

  // fetch providers on mount
  useEffect(() => {
    dispatch(
      fetchProviders({ searchTerm: !!preSelectedCompany ? '' : userZip, isAuthedUser: !!userId })
    );
  }, [userZip, userId, preSelectedCompany, dispatch]);

  // move to next screen on success
  useEffect(() => {
    if (funeralHomeSelected) {
      dispatch(resetSelectedFuneralHome());
      dispatch(saveLastScreen({ screen: 'getUserPhone' }));
    }
  }, [funeralHomeSelected]);

  // formik vars/schema
  const initialValues = {
    funeralHome: !!selectedHomeId ? selectedHomeId : ''
  };
  const validationSchema = Yup.object().shape({
    funeralHome: Yup.string().required()
  });

  const onSubmit = values => {
    const funeralHome = values?.funeralHome;
    dispatch(saveFuneralHomeToUser({ funeralHome }));
  };

  const handleGoBack = () => {
    dispatch(saveLastScreen({ screen: 'selectPackage' }));
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
    >
      {({ values, isValid, handleSubmit }) => (
        <>
          <StepContainer
            showLoader={isFetching || isFetchingProviders}
            evelynArray={[
              'Choosing a funeral home makes everything so much easier for your family because they’ll know exactly who to call when you pass away.',
              {
                content: 'Please select your preferred funeral home:',
                style: 'header'
              }
            ]}
            {...{ location }}
          >
            <div className={selectProviderClass}>
              <Form
                onKeyDown={e => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
                className={classNames({ 'search-bottom': !!preSelectedCompany })}
              >
                {!!servedFamilyProviders.length
                  ? servedFamilyProviders
                      .slice(0, familyProvidersToShow)
                      .map((provider, idx) => (
                        <ProviderCard
                          {...{ provider, headerColor, fillColor, packageId, stateWarning }}
                          fieldName="funeralHome"
                          value={provider?._id}
                          key={provider?.name}
                          isChecked={values?.funeralHome === provider?._id}
                          values={values}
                          headerCopy={`served the ${provider?.servedFamily} family`}
                          extraMargin={familyProvidersToShow - 1 !== idx ? true : false}
                        />
                      ))
                  : null}
                {servedFamilyProviders.length > 1 &&
                  servedFamilyProviders.length !== familyProvidersToShow && (
                    <div
                      className={showMoreFamilyProvidersClass}
                      onClick={() => setFamilyProvidersToShow(servedFamilyProviders.length)}
                    >
                      {`Show ${
                        servedFamilyProviders.length - 1
                      } other homes people you know have used`}
                      <img src={chevronDown} alt="chev-down" />
                    </div>
                  )}
                {!preSelectedCompany ? (
                  <FindProviders
                    initialFormValue={userZip}
                    isAuthedUser={!!userId}
                    isPreSelectedCompany={!!preSelectedCompany}
                  />
                ) : null}
                {!!providersInRange.length ? (
                  providersInRange.map((provider, idx) => (
                    <LazyLoad
                      height={150}
                      key={provider?._id}
                      offset={[-100, 100]}
                      scrollContainer=".purchase-step-content"
                    >
                      {provider !== 'WhatIfCard' ? (
                        <ProviderCard
                          {...{ provider, headerColor, fillColor, idx, packageId, stateWarning }}
                          fieldName="funeralHome"
                          value={provider?._id}
                          key={provider?.name}
                          isChecked={values?.funeralHome === provider?._id}
                          values={values}
                          headerCopy={idx === 0 ? 'most loved near you' : ''}
                        />
                      ) : (
                        <WhatIfCard
                          titleCopy="Not sure which funeral home you want to work with?"
                          bodyCopy="No worries, you can always change your mind! Our care team will always be here to help you switch your plan to a new provider of your choosing."
                        />
                      )}
                    </LazyLoad>
                  ))
                ) : (
                  <NoResultsCard />
                )}
                {!!preSelectedCompany ? (
                  <FindProviders
                    initialFormValue={userZip}
                    isAuthedUser={!!userId}
                    isPreSelectedCompany={!!preSelectedCompany}
                  />
                ) : null}
              </Form>
              {isTexasResident && (
                <p className={texasDisclaimerClass}>
                  If you are a Texas resident, by continuing you agree to state specific policies.
                  {` `}
                  <a target="_blank" rel="noopener noreferrer" href={stateWarning.url}>
                    View here.
                  </a>
                </p>
              )}
            </div>
          </StepContainer>
          <FixedFooter
            backCopy="Back"
            backAction={handleGoBack}
            isValid={isValid}
            nextCopy="Next"
            nextAction={handleSubmit}
          />
        </>
      )}
    </Formik>
  );
};

export default SelectProvider;
